import {
  CalendarOptions,
  FormatterInput,
  SlotLabelContentArg,
} from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import dayjs from 'dayjs';
import { forwardRef, useCallback, useState } from 'react';
import { DEFAULT_TIME } from 'src/constants';
import { useParamQuery } from 'src/hooks';

import { fullCalendar } from '../../../package.json';
import {
  ECalendarDayFormat,
  ECalendarHourFormat,
  ECalendarMeridiemFormat,
  ECalendarMonthFormat,
  ECalendarViewsId,
  ECalendarWeekdayFormat,
  ECalendarYearFormat,
  EHeaderToolbar,
} from './Calendar.interface';
import {
  PreviewButton,
  StyledCalendarWrapper,
  WeekdayHeader,
  WeekdayText,
} from './Calendar.styled';
// This is the config for the column header labels format.
const calendarViewsLabelFormats = {
  year: [{ month: ECalendarMonthFormat.long }],
  month: [{ day: ECalendarDayFormat.numeric }],
  week: [{ weekday: ECalendarWeekdayFormat.long }],
};
const titleFormats = {
  weekView: {
    year: ECalendarYearFormat.numeric,
    month: ECalendarMonthFormat.long,
    day: ECalendarDayFormat.numeric,
  },
};
// Configuration for individual views of the calendar like year,month and days
const calendarViews = (
  previewClick?: (date: string) => void,
  onHoverDateChange?: (date: string, hasEvents: boolean) => void,
  hasEventsForHoveredDate?: boolean,
  hoveredDate?: string | null,
  individualLocationCode?: string,
) => ({
  [ECalendarViewsId.resourceTimelineYear]: {
    slotLabelFormat: calendarViewsLabelFormats.year as FormatterInput,
    slotLabelInterval: { months: 1 },
    slotMinWidth: 130,
  },
  [ECalendarViewsId.resourceTimelineMonth]: {
    slotLabelFormat: calendarViewsLabelFormats.month as FormatterInput,
    slotMinWidth: 70,
    showNonCurrentDates: false,
  },
  [ECalendarViewsId.resourceTimelineWeek]: {
    slotLabelFormat: calendarViewsLabelFormats.week as FormatterInput,
    slotDuration: { days: 1 },
    slotMinWidth: 130,
    titleFormat: titleFormats.weekView as FormatterInput,
    slotLabelContent: (args: SlotLabelContentArg) => {
      const localDate = args.date;
      const isoLocalDate =
        localDate.getFullYear() +
        '-' +
        String(localDate.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(localDate.getDate()).padStart(2, '0');
      const dayStart = dayjs(localDate).startOf('day').toDate();
      const dayEnd = dayjs(localDate).endOf('day').toDate();
      const handleMouseEnter = () => {
        const calendarApi = args.view.calendar;
        const today = dayjs().startOf('day');
        const hoveredDay = dayjs(localDate).startOf('day');
        const isTooFarInPast = hoveredDay.isBefore(today.subtract(30, 'day'));
        const maxFutureDay = today.add(3, 'year');
        const isTooFarInFuture = hoveredDay.isAfter(maxFutureDay);
        if (isTooFarInPast || isTooFarInFuture) {
          onHoverDateChange && onHoverDateChange(isoLocalDate, false);

          return;
        }
        const hasAnyEvent = calendarApi.getEvents().some((event) => {
          const range = (event as any)?._instance?.range;
          if (!range) return false;

          return range.start <= dayEnd && range.end >= dayStart;
        });
        onHoverDateChange && onHoverDateChange(isoLocalDate, hasAnyEvent);
      };

      return (
        <WeekdayHeader
          data-testid="weekday-header"
          onMouseEnter={handleMouseEnter}>
          <WeekdayText
            className="preview-text"
            hasPreview={!individualLocationCode}>
            {args.text}
          </WeekdayText>
          {!individualLocationCode && (
            <PreviewButton
              aria-label={`Preview`}
              disabled={!hasEventsForHoveredDate}
              data-testid="week-preview-button"
              onClick={() =>
                hasEventsForHoveredDate &&
                previewClick &&
                previewClick(hoveredDate as string)
              }>
              <PlayCircleOutlineIcon fontSize="small" />
              Preview
            </PreviewButton>
          )}
        </WeekdayHeader>
      );
    },
  },
});
const today = new Date().toLocaleDateString('en-CA');
const resourceOrderPrimaryKey = 'default_start_time';

export const Calendar = forwardRef<
  FullCalendar | null,
  Partial<CalendarOptions> & {
    previewClick?: (date: string) => void;
  }
>(({ previewClick, ...props }, ref) => {
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [hasEventsForHoveredDate, setHasEventsForHoveredDate] =
    useState<boolean>(false);
  const handleHoverDateChange = useCallback(
    (date: string, hasEvents: boolean) => {
      setHoveredDate(date);
      setHasEventsForHoveredDate(hasEvents);
    },
    [],
  );

  const { getParamsValue } = useParamQuery();
  const groupIDs = getParamsValue('groupID').filter(
    (id) => id && id !== 'undefined',
  );
  const individualLocationCode = groupIDs[0] ?? '';

  return (
    <StyledCalendarWrapper>
      <FullCalendar
        ref={ref}
        schedulerLicenseKey={fullCalendar.licenseKey ?? ''}
        headerToolbar={{
          left: `${EHeaderToolbar.prev},${EHeaderToolbar.next},${EHeaderToolbar.today}`,
          center: `${EHeaderToolbar.title}`,
          right: `${ECalendarViewsId.resourceTimelineYear},${ECalendarViewsId.resourceTimelineMonth},${ECalendarViewsId.resourceTimelineWeek}`,
        }}
        plugins={[resourceTimelinePlugin]}
        editable={false}
        eventDurationEditable={false}
        weekends={true}
        initialDate={today}
        expandRows={true}
        height={'100%'}
        initialView={ECalendarViewsId.resourceTimelineMonth}
        scrollTime={DEFAULT_TIME}
        scrollTimeReset={true}
        slotLabelFormat={[
          { weekday: ECalendarWeekdayFormat.long },
          {
            hour: ECalendarHourFormat.numeric,
            meridiem: ECalendarMeridiemFormat.short,
          },
        ]}
        contentHeight={600}
        views={calendarViews(
          previewClick,
          handleHoverDateChange,
          hasEventsForHoveredDate,
          hoveredDate,
          individualLocationCode,
        )}
        eventOrderStrict={true}
        resourceOrder={resourceOrderPrimaryKey}
        // moreLinkText can't be tested in unit test as it depends on the real dom
        moreLinkText={(num) => `+${num}`}
        {...props}
      />
    </StyledCalendarWrapper>
  );
});
